import React, { useState, useEffect } from 'react';
import PreviousWorks from '../components/PreviousWorks';
import { ACTIVITY_CATEGORIES } from '../utils/activities';
import { ArrowLeft, CheckCircle, XCircle, FileText, MapPin, Building2, Eye, Camera, Upload, Trash2, User, Mail, Phone } from 'lucide-react';

import { useNavigate, useParams } from 'react-router-dom';
import { FILE_BASE_URL, API_BASE_URL } from '../api/config';
import API from '../api';

import { showSuccess, showError, redirectPage } from '../utils/notification';
import { useAuth } from '../auth/AuthContext';

// Activity categories used to expand the comma-encoded `activity_ids` stored
// on application_hdr (e.g. "1:Strengthening of bund,4:Fencing").

const formatDate = (v) => (v ? new Date(v).toLocaleDateString('en-IN') : '-');

// Status chip colours for the dark page header.
const headerStatusClass = (status) => {
  const u = String(status || '').toUpperCase();
  if (u === 'APPROVED' || u === 'DEEMED_APPROVED') return 'bg-emerald-100 text-emerald-800 border-emerald-500';
  if (u === 'REJECTED') return 'bg-rose-100 text-rose-800 border-rose-500';
  if (u === 'RETURNED') return 'bg-amber-100 text-amber-800 border-amber-500';
  if (u === 'PENDING') return 'bg-orange-100 text-orange-800 border-orange-500';
  return 'bg-slate-100 text-slate-700 border-slate-400';
};

// FRS: SLA is 10 working days from the date the application was received at
// the current level. For L1 the clock starts at submission; for L2 it starts
// when L1 forwards. We approximate by using each chain row's action_date
// (when the row last changed) or, for L1 only, the application created_at.
const SLA_DAYS = 10;
const addWorkingDays = (start, days) => {
  const d = new Date(start);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) added += 1;
  }
  return d;
};
const slaForStep = (app, step) => {
  if (!app || !step) return null;
  const start = step.level === 1 ? app.created_at : step.action_date || app.created_at;
  if (!start) return null;
  const due = addWorkingDays(start, SLA_DAYS);
  const now = new Date();
  const remainingMs = due - now;
  const daysLeft = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
  return { due, daysLeft, overdue: remainingMs < 0 };
};

const stripNocPrefix = (raw) => {
  if (!raw) return '';
  const m = String(raw).match(/(\d+)$/);
  return m ? m[1] : '';
};

export default function WorkflowPage() {
  const { id: rawId } = useParams();
  const navigate = useNavigate();

  const applicationId = stripNocPrefix(rawId);

  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [tank, setTank] = useState(null);
  const [ngo, setNgo] = useState(null);
  const [department, setDepartment] = useState(null);
  const [district, setDistrict] = useState(null);
  const [chain, setChain] = useState([]);
  const [chainUsers, setChainUsers] = useState({});
  const [docs, setDocs] = useState([]);
  const [restorationPhotos, setRestorationPhotos] = useState([]);
  const [restorationBusy, setRestorationBusy] = useState('');
  const [showNGOProfile, setShowNGOProfile] = useState(false);
  const [actionMode, setActionMode] = useState(null); // 'approve' | 'reject' | 'return' | null
  const [comments, setComments] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');

  // FRS: mandatory reasons listed in a dropdown for Reject / Return.
  // Department-provided list will replace this once finalised.
  const REJECT_REASONS = [
    'Documents incomplete or invalid',
    'Application details do not match site records',
    'Waterbody not eligible under current scheme',
    'Duplicate application',
    'Other',
  ];
  const RETURN_REASONS = [
    'Clarification required on activity scope',
    'Additional supporting documents needed',
    'Revised cost estimate required',
    'Field-visit feedback to be incorporated',
    'Other',
  ];

  // Auth from context (pen-test remediation — no localStorage).
  const { user: userInfo, role: userRole, isReady: authReady, isAuthenticated } = useAuth();
  const userId = userInfo?.user_id;

  useEffect(() => {
    if (authReady && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!authReady || !isAuthenticated) return;
    if (!applicationId) {
      showError('Invalid application id');
      return;
    }

    const safeGet = (url) =>
      API.get(url)
        .then((r) => r?.data?.data ?? null)
        .catch((err) => {
          console.error(`GET ${url} failed:`, err?.response?.data || err.message);
          return null;
        });

    setLoading(true);
    safeGet(`/applications/${applicationId}`).then(async (app) => {
      if (!app) {
        showError('Application not found');
        setLoading(false);
        return;
      }
      setApplication(app);

      // Fetch related rows in parallel; each is independent.
      const [tankRows, ngoRow, deptRow, districtRow, chainRows, docRows] = await Promise.all([
        safeGet(`/tanks?tank_id=${app.tank_id}`),
        safeGet(`/users/${app.applied_by}`),
        safeGet(`/departments/${app.department_id}`),
        safeGet(`/districts/${app.district_id}`),
        safeGet(`/applications/${applicationId}/chain`),
        safeGet(`/applications/${applicationId}/docs`),
      ]);

      setTank(Array.isArray(tankRows) ? tankRows[0] || null : tankRows);
      setNgo(ngoRow);
      setDepartment(deptRow);
      setDistrict(districtRow);
      let chainArr = Array.isArray(chainRows) ? chainRows : [];

      // Self-heal: if no chain exists (application predates the workflow code,
      // or was created before officer mappings were seeded), try to seed it
      // silently and re-fetch. The endpoint is idempotent.
      if (chainArr.length === 0) {
        try {
          await API.post(`/applications/${applicationId}/seed-chain`);
          const reseed = await safeGet(`/applications/${applicationId}/chain`);
          chainArr = Array.isArray(reseed) ? reseed : [];
        } catch (err) {
          console.warn('auto seed-chain failed:', err?.response?.data || err.message);
        }
      }

      setChain(chainArr);
      setDocs(Array.isArray(docRows) ? docRows : []);

      // Fetch restoration photos (may 403 if viewer isn't allowed — that's fine)
      const restRes = await safeGet(`/restoration/${applicationId}`);
      if (restRes && Array.isArray(restRes.photos)) setRestorationPhotos(restRes.photos);

      // Resolve approver names lazily.
      const approverIds = Array.from(new Set(chainArr.map((r) => r.user_id).filter(Boolean)));
      const userMap = {};
      await Promise.all(
        approverIds.map((uid) =>
          safeGet(`/users/${uid}`).then((u) => {
            if (u) userMap[uid] = u;
          })
        )
      );
      setChainUsers(userMap);
      setLoading(false);
    });
  }, [applicationId, navigate, authReady, isAuthenticated]);

  const docMeta = (() => {
    try { return JSON.parse(application?.doc_path || '{}'); } catch { return {}; }
  })();

  const activitiesByCategory = (() => {
    const out = {};
    String(application?.activity_ids || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((tok) => {
        const [catId, sub] = tok.split(':');
        const key = ACTIVITY_CATEGORIES[catId] || `Category ${catId}`;
        out[key] = out[key] || [];
        if (sub) out[key].push(sub);
      });
    return out;
  })();

  const activeStep = chain.find((s) => String(s.status).toUpperCase() === 'PENDING') || null;
  // Action buttons appear only when the *currently pending* step is assigned
  // to this user AND the application is still in flight (not APPROVED/REJECTED).
  const appStatusUpper = String(application?.status || '').toUpperCase();
  const isTerminal = appStatusUpper === 'APPROVED' || appStatusUpper === 'REJECTED';
  const isMyTurn =
    !isTerminal && !!(activeStep && userId && Number(activeStep.user_id) === Number(userId));

  const openAction = (mode) => {
    setActionMode(mode);
    setComments('');
    setReason('');
    setActionError('');
  };

  const cancelAction = () => {
    setActionMode(null);
    setComments('');
    setReason('');
    setActionError('');
  };

  const submitAction = async () => {
    // FRS: Reject and Return require a mandatory reason from the dropdown plus remarks.
    if ((actionMode === 'reject' || actionMode === 'return') && !reason) {
      setActionError('Reason is required.');
      return;
    }
    if ((actionMode === 'reject' || actionMode === 'return') && !comments.trim()) {
      setActionError('Remarks are required.');
      return;
    }
    setSubmitting(true);
    setActionError('');
    try {
      const endpoint = actionMode === 'return' ? 'return' : actionMode;
      const url = `/applications/${applicationId}/${endpoint}`;
      const body = { comments: comments.trim() };
      if (reason) body.reason = reason;
      const res = await API.post(url, body);
      if (res?.data?.success) {
        const msg =
          actionMode === 'approve' ? 'Approved'
          : actionMode === 'return' ? (Number(activeStep?.level) > 1 ? 'Returned to L1' : 'Returned to applicant')
          : 'Rejected';
        showSuccess(res.data.message || msg);
        redirectPage('/dashboard');
      } else {
        setActionError(res?.data?.message || `${actionMode} failed`);
      }
    } catch (err) {
      setActionError(err?.response?.data?.message || `${actionMode} failed`);
    } finally {
      setSubmitting(false);
    }
  };

  const stepStatusColor = (status) => {
    const s = String(status).toUpperCase();
    if (s === 'APPROVED') return 'bg-green-500';
    if (s === 'REJECTED') return 'bg-red-500';
    if (s === 'PENDING') return 'bg-orange-400';
    return 'bg-gray-300';
  };

  return (
    <div className="bg-slate-100 min-h-screen font-['Inter',sans-serif] pt-16 sm:pt-20">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b-4 border-brand-accent">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex items-center py-3">
            <button
              onClick={() => (window.location.href = '/dashboard')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mr-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-gray-500">Tamil Nadu NOC Portal</span>
              <span className="text-gray-300">→</span>
              <span className="text-gray-500">Dashboard</span>
              <span className="text-gray-300">→</span>
              <span className="text-brand-700 font-medium">NOC Workflow</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5">
        {loading && (
          <div className="bg-white border border-slate-200 p-8 text-center text-gray-500">Loading…</div>
        )}

        {!loading && application && (
          <>
            {/* Header — NOC ID, status, SLA, submitted date */}
            <div className="bg-brand-800 border-l-8 border-brand-accent text-white px-5 py-4 mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-brand-100">NOC Application Review</p>
                <h1 className="text-xl sm:text-2xl font-semibold mt-0.5">
                  NOC{String(application.application_id).padStart(3, '0')}
                </h1>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className={`px-2.5 py-1 text-xs font-semibold uppercase border-l-4 ${headerStatusClass(application.status)}`}>
                  {application.status}
                </span>
                {(() => {
                  const sla = slaForStep(application, activeStep);
                  if (!sla) return null;
                  const cls = sla.overdue
                    ? 'bg-rose-600 text-white'
                    : sla.daysLeft <= 2
                    ? 'bg-orange-500 text-white'
                    : 'bg-white/15 text-white';
                  return (
                    <span className={`px-2.5 py-1 text-xs font-semibold ${cls}`}>
                      SLA: {sla.overdue ? `${Math.abs(sla.daysLeft)}d overdue` : `${sla.daysLeft}d left`}
                    </span>
                  );
                })()}
                <span className="text-brand-100 sm:ml-2">
                  Submitted <b className="text-white font-medium">{formatDate(application.created_at)}</b>
                </span>
              </div>
            </div>

            {/* Approval chain */}
            {chain.length > 0 && (
              <div className="bg-white border border-slate-200 mb-5">
                <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50">
                  <h3 className="text-sm font-semibold text-slate-800">Approval Chain</h3>
                </div>
                <div className="p-4 flex flex-col md:flex-row md:items-stretch gap-3">
                  {chain.slice().sort((a, b) => Number(a.level) - Number(b.level)).map((step, i, arr) => {
                    const st = String(step.status || '').toUpperCase();
                    const tone = st === 'APPROVED'
                      ? 'border-emerald-600 bg-emerald-50'
                      : st === 'REJECTED'
                      ? 'border-rose-600 bg-rose-50'
                      : st === 'RETURNED'
                      ? 'border-amber-500 bg-amber-50'
                      : st === 'PENDING'
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-slate-300 bg-slate-50';
                    const u = chainUsers[step.user_id] || {};
                    const rawName = step.full_name || u.full_name || '';
                    const name = rawName && !/edit after 1st login/i.test(rawName) ? rawName : (step.email_id || u.email_id || 'Officer not mapped');
                    return (
                      <React.Fragment key={step.app_temp_id || step.level}>
                        <div className={`flex-1 border-l-4 ${tone} px-4 py-3`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-bold text-slate-700">LEVEL {step.level}</span>
                            <span className="text-xs font-semibold text-slate-700">{st || 'WAITING'}</span>
                          </div>
                          <p className="text-sm font-medium text-slate-900 mt-1 truncate" title={name}>{name}</p>
                          {step.action_date && (
                            <p className="text-xs text-slate-500 mt-0.5">{formatDate(step.action_date)}</p>
                          )}
                          {step.comments && (
                            <p className="text-xs text-slate-600 mt-1.5 italic line-clamp-2" title={step.comments}>“{step.comments}”</p>
                          )}
                        </div>
                        {i < arr.length - 1 && (
                          <div className="hidden md:flex items-center text-slate-400">→</div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Applicant */}
            <div className="bg-white border border-slate-200 mb-5 px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-brand-700 text-white flex items-center justify-center flex-shrink-0 font-semibold">
                  {(ngo?.full_name || 'A').trim().charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-brand-600 font-semibold">Applicant</p>
                  <p className="text-base font-semibold text-gray-900 truncate">
                    {ngo?.full_name || `User #${application.applied_by}`}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-600">
                    {ngo?.email_id && (
                      <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{ngo.email_id}</span>
                    )}
                    {ngo?.mobile_number && (
                      <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{ngo.mobile_number}</span>
                    )}
                    {ngo?.user_type && (
                      <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />{ngo.user_type}</span>
                    )}
                  </div>
                </div>
              </div>
              {ngo && (
                <button
                  onClick={() => setShowNGOProfile(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-700 bg-white border border-brand-600 hover:bg-brand-50"
                >
                  <Eye className="w-4 h-4" />
                  View Full Profile
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
              {/* Tank details — key/value table */}
              <div className="bg-white border border-slate-200">
                <div className="px-4 py-2.5 bg-brand-600 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-white" />
                  <h3 className="text-sm font-semibold text-white">Tank Details</h3>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {[
                      ['Unique Tank ID', application.tank_id ? `TN-${String(application.tank_id).padStart(6, '0')}` : '-'],
                      ['Tank Name', tank?.water_body_name || `Tank #${application.tank_id}`],
                      ['Department', department?.department_name || tank?.owned_by || '-'],
                      ['District', district?.district_name || tank?.district_name || '-'],
                      ['Block', tank?.block_name || '-'],
                      ['Village', tank?.village_or_town_name || '-'],
                      ...(docMeta.lumpsumAmount ? [['Estimate Amount', `₹${docMeta.lumpsumAmount}`]] : []),
                      ...(application.remarks ? [['Remarks', application.remarks]] : []),
                    ].map(([k, v]) => (
                      <tr key={k} className="odd:bg-white even:bg-brand-50 border-b border-slate-100 last:border-0">
                        <td className="px-4 py-2 w-40 text-xs font-semibold uppercase tracking-wide text-slate-500 align-top">{k}</td>
                        <td className={`px-4 py-2 text-slate-900 ${k === 'Estimate Amount' ? 'font-bold text-brand-700' : k === 'Tank Name' ? 'font-semibold' : ''}`}>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Activities */}
              <div className="bg-white border border-slate-200">
                <div className="px-4 py-2.5 bg-violet-600 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-white" />
                  <h3 className="text-sm font-semibold text-white">Activities</h3>
                </div>
                {Object.keys(activitiesByCategory).length === 0 ? (
                  <p className="p-4 text-sm text-gray-500">No activities recorded.</p>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {Object.entries(activitiesByCategory).map(([cat, subs]) => (
                        <tr key={cat} className="odd:bg-white even:bg-violet-50 border-b border-slate-100 last:border-0">
                          <td className="px-4 py-2.5 w-40 text-xs font-semibold uppercase tracking-wide text-slate-500 align-top">{cat}</td>
                          <td className="px-4 py-2.5">
                            {subs.length === 0 ? (
                              <span className="text-xs text-gray-500 italic">Selected (no sub-items)</span>
                            ) : (
                              <div className="flex flex-wrap gap-1.5">
                                {subs.map((sub, i) => (
                                  <span key={`${sub}-${i}`} className="inline-flex px-2 py-0.5 text-xs font-medium bg-violet-100 text-violet-800">
                                    {sub}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Works already carried out on this water body (other NOCs) —
                visible to the applicant, L1, L2, Dept Admin and State users. */}
            {application.tank_id && (
              <PreviousWorks tankId={application.tank_id} excludeApplicationId={application.application_id} />
            )}

            {/* Documents */}
            <div className="bg-white border border-slate-200 mb-5">
              <div className="px-4 py-2.5 bg-teal-600 flex items-center gap-2">
                <FileText className="w-4 h-4 text-white" />
                <h3 className="text-sm font-semibold text-white">Submitted Documents ({docs.length})</h3>
              </div>
              {docs.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">No documents uploaded.</p>
              ) : (
                <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {docs.map((d) => {
                    const url = `${FILE_BASE_URL}/${String(d.stored_path).replace(/^uploads\//, '')}`;
                    const labelMap = {
                      before_photo: 'Before Photo',
                      project_proposal: 'Project Proposal',
                      kml_file: 'KML File',
                    };
                    const ext = (d.original_name || '').split('.').pop().toUpperCase().slice(0, 4) || 'FILE';
                    const isImage = /^image\//i.test(d.mime_type || '') ||
                      /\.(png|jpe?g|gif|webp)$/i.test(d.original_name || '');
                    const isPdf = /pdf/i.test(d.mime_type || '') || /\.pdf$/i.test(d.original_name || '');
                    const badge = isImage
                      ? 'bg-emerald-100 text-emerald-800'
                      : isPdf
                      ? 'bg-rose-100 text-rose-800'
                      : 'bg-slate-100 text-slate-700';
                    return (
                      <a
                        key={d.doc_id}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="group border border-slate-200 hover:border-teal-600 hover:shadow-md transition block"
                        title={d.original_name}
                      >
                        {isImage ? (
                          <img src={url} alt={d.original_name} className="w-full h-24 object-cover bg-slate-50" loading="lazy" />
                        ) : (
                          <div className="w-full h-24 bg-slate-50 flex items-center justify-center">
                            <FileText className="w-8 h-8 text-slate-400" />
                          </div>
                        )}
                        <div className="px-2 py-1.5 border-t border-slate-100 flex items-center gap-1.5">
                          <span className={`text-[10px] font-bold px-1 ${badge}`}>{ext}</span>
                          <span className="text-xs font-medium text-gray-800 truncate">{labelMap[d.doc_type] || d.doc_type}</span>
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Action buttons (only for the active approver) */}
            {isMyTurn && (
              <div className="bg-white border border-slate-200 border-t-4 border-t-brand-600 p-4 sm:p-6 mb-5">
                <h3 className="text-base font-semibold text-gray-900 mb-4">Take Action</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <button
                    onClick={() => openAction('approve')}
                    disabled={submitting}
                    className={`flex items-center justify-center space-x-3 p-3 text-white shadow-sm transition-colors ${
                      actionMode === 'approve'
                        ? 'bg-green-700 ring-2 ring-green-300'
                        : 'bg-green-600 hover:bg-green-700'
                    } disabled:opacity-50`}
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Approve</span>
                  </button>
                  <button
                    onClick={() => openAction('return')}
                    disabled={submitting}
                    className={`flex items-center justify-center space-x-3 p-3 text-white shadow-sm transition-colors ${
                      actionMode === 'return'
                        ? 'bg-amber-600 ring-2 ring-amber-300'
                        : 'bg-amber-500 hover:bg-amber-600'
                    } disabled:opacity-50`}
                  >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="font-medium">{Number(activeStep?.level) > 1 ? 'Return to L1' : 'Return to Applicant'}</span>
                  </button>
                  <button
                    onClick={() => openAction('reject')}
                    disabled={submitting}
                    className={`flex items-center justify-center space-x-3 p-3 text-white shadow-sm transition-colors ${
                      actionMode === 'reject'
                        ? 'bg-red-700 ring-2 ring-red-300'
                        : 'bg-red-600 hover:bg-red-700'
                    } disabled:opacity-50`}
                  >
                    <XCircle className="w-5 h-5" />
                    <span className="font-medium">Reject</span>
                  </button>
                </div>
                {/* L1/L2 wording removed; users see only the action verbs. */}

                {actionMode && (
                  <div className="mt-5 border-t border-gray-200 pt-5 space-y-3">
                    {(actionMode === 'reject' || actionMode === 'return') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {actionMode === 'reject' ? 'Rejection reason' : 'Return reason'}{' '}
                          <span className="text-red-600">*</span>
                        </label>
                        <select
                          value={reason}
                          onChange={(e) => {
                            setReason(e.target.value);
                            if (actionError) setActionError('');
                          }}
                          className="w-full border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600"
                        >
                          <option value="">— Select a reason —</option>
                          {(actionMode === 'reject' ? REJECT_REASONS : RETURN_REASONS).map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {actionMode === 'approve'
                          ? (Number(activeStep?.level) === 1
                              ? 'Approval remarks (in consultation with L1A officer, if applicable)'
                              : 'Approval remarks (optional)')
                          : actionMode === 'return'
                          ? (Number(activeStep?.level) > 1 ? 'Remarks to L1 officer ' : 'Remarks to applicant ')
                          : 'Rejection remarks '}
                        {actionMode !== 'approve' && <span className="text-red-600">*</span>}
                      </label>
                      <textarea
                        value={comments}
                        onChange={(e) => {
                          setComments(e.target.value);
                          if (actionError) setActionError('');
                        }}
                        rows={4}
                        placeholder={
                          actionMode === 'approve'
                            ? 'Add any notes for the next level…'
                            : actionMode === 'return'
                            ? 'Tell the applicant what to revise or clarify…'
                            : 'Explain why this application is being rejected…'
                        }
                        className="w-full border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600"
                      />
                    </div>
                    {actionMode === 'return' && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">
                        Note: an application can be returned only once. On resubmission by the applicant the SLA timeline resumes.
                      </p>
                    )}
                    {actionError && (
                      <p className="text-sm text-red-600">{actionError}</p>
                    )}
                    <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                      <button
                        onClick={cancelAction}
                        disabled={submitting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={submitAction}
                        disabled={submitting}
                        className={`px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                          actionMode === 'approve'
                            ? 'bg-green-600 hover:bg-green-700'
                            : actionMode === 'return'
                            ? 'bg-amber-500 hover:bg-amber-600'
                            : 'bg-red-600 hover:bg-red-700'
                        }`}
                      >
                        {submitting
                          ? 'Submitting…'
                          : actionMode === 'approve'
                          ? 'Confirm Approval'
                          : actionMode === 'return'
                          ? 'Confirm Return'
                          : 'Confirm Rejection'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isMyTurn && activeStep && (
              <div className="bg-amber-50 border border-amber-200 border-l-4 mb-5 p-4 text-sm text-amber-800">
                {['L1 A', 'L2 A', 'Master'].includes(userInfo?.role_type)
                  ? <>You are viewing this application in <b>read-only</b> mode as <b>{userInfo.role_type}</b>. Only the assigned Level-1 / Level-2 approvers can take action.</>
                  : <>This application is currently under review by the assigned department officer. Action buttons appear only for the officer whose turn it is.</>}
              </div>
            )}

            {!activeStep && chain.length === 0 && (
              <div className="bg-amber-50 border border-amber-300 border-l-4 mb-5 p-4 sm:p-5 text-sm text-amber-900">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center font-bold text-sm">!</div>
                  <div className="flex-1">
                    <p className="font-semibold mb-1">Approval chain could not be built — officer mapping pending from department</p>
                    <p className="leading-relaxed">
                      This tank belongs to the{' '}
                      <b>{department?.department_name || tank?.owned_by || '—'}</b> department in the{' '}
                      <b>{district?.district_name || tank?.district_name || '—'}</b> district
                      {tank?.section_name && tank.section_name !== '-' ? (
                        <> (section: <b>{tank.section_name}</b>)</>
                      ) : null}.
                      No Level-1 / Level-2 officer has been mapped for this combination yet, so
                      the application cannot be routed.
                    </p>
                    <p className="leading-relaxed mt-2">
                      <b>Action required from the department:</b> please provide the officer list
                      (name, official email, designation) for this department + district
                      combination so the portal can route applications.
                    </p>
                    <p className="text-xs text-amber-700 mt-2">
                      Reference: Application <b>NOC{String(application.application_id).padStart(3, '0')}</b>.
                      Until officers are onboarded, this application will remain unrouted.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!activeStep && chain.length > 0 && (() => {
              // Am I the L2 officer for this application? (Completion
              // certificate is issued by the final approver, per client
              // requirement — was earlier gated on L1.)
              const sortedChain = chain.slice().sort((a, b) => Number(a.level) - Number(b.level));
              const l2Step = sortedChain[sortedChain.length - 1];
              const isL2Officer = l2Step && Number(l2Step.user_id) === Number(userId);
              const isApproved = appStatusUpper === 'APPROVED' || appStatusUpper === 'DEEMED_APPROVED';
              // Has the completion certificate already been issued?
              const completionIssued = !!application?.completion_issued_at;
              const issuedOn = completionIssued
                ? new Date(application.completion_issued_at).toLocaleDateString('en-IN')
                : null;
              // NGO must request the certificate first; L2 then approves it.
              const isApplicant = Number(application?.applied_by) === Number(userId);
              const completionRequested = !!application?.completion_requested_at;
              // Before / During / After photos are all required before requesting.
              const missingStages = [['BEFORE', 'Before Work'], ['DURING', 'During Work'], ['AFTER', 'After Work']]
                .filter(([k]) => !restorationPhotos.some((p) => String(p.stage).toUpperCase() === k))
                .map(([, label]) => label);
              const photosComplete = missingStages.length === 0;
              const requestedOn = completionRequested
                ? new Date(application.completion_requested_at).toLocaleDateString('en-IN')
                : null;
              const handleRequestCompletion = async () => {
                if (!photosComplete) {
                  showError(`Upload restoration photos for: ${missingStages.join(', ')} first`);
                  return;
                }
                if (!window.confirm('Request the Work Completion Certificate? Do this only after all restoration works are finished. The Level 2 officer will verify and approve it.')) return;
                try {
                  await API.post(`/applications/${applicationId}/completion-certificate/request`);
                  showSuccess('Completion certificate requested');
                  setTimeout(() => window.location.reload(), 600);
                } catch (err) {
                  showError(err?.response?.data?.message || 'Failed to request completion certificate');
                }
              };
              const handleGenerateCompletion = async () => {
                if (!window.confirm('Approve the request and generate the Work Completion Certificate? Use this only after the on-ground work has been verified as complete. This cannot be undone.')) return;
                try {
                  await API.post(`/applications/${applicationId}/completion-certificate/issue`);
                  showSuccess('Completion certificate issued');
                  window.open(`${API_BASE_URL}/applications/${applicationId}/completion-certificate`, '_blank');
                  setTimeout(() => window.location.reload(), 600);
                } catch (err) {
                  showError(err?.response?.data?.message || 'Failed to issue completion certificate');
                }
              };
              return (
                <div className="bg-emerald-50 border border-emerald-200 border-l-4 mb-5 p-4 text-sm text-emerald-800 flex items-center justify-between flex-wrap gap-3">
                  <span>
                    Workflow complete — every level has acted on this application.
                    {completionIssued && (
                      <span className="block text-xs text-teal-700 mt-1">
                        Work Completion Certificate issued on <b>{issuedOn}</b>.
                      </span>
                    )}
                    {isApproved && !completionIssued && completionRequested && (
                      <span className="block text-xs text-amber-700 mt-1">
                        Completion certificate requested by the applicant on <b>{requestedOn}</b> — awaiting Level 2 approval.
                      </span>
                    )}
                    {isApproved && !completionIssued && !completionRequested && isL2Officer && (
                      <span className="block text-xs text-slate-600 mt-1">
                        The applicant has not requested the completion certificate yet.
                      </span>
                    )}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {isApproved && (
                      <button
                        onClick={() => window.open(`${API_BASE_URL}/applications/${applicationId}/certificate`, '_blank')}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700"
                      >
                        View NOC Certificate
                      </button>
                    )}

                    {/* Completion certificate flow:
                        - NGO (applicant) → "Request Completion Certificate" once the NOC is approved
                        - L2 officer → "Approve & Generate" only after the NGO has requested
                        - Already issued → "View Completion Certificate" to everyone */}
                    {isApproved && !completionIssued && !completionRequested && isApplicant && (
                      <div className="flex flex-col items-end gap-1">
                        <button
                          onClick={handleRequestCompletion}
                          disabled={!photosComplete}
                          className={`inline-flex items-center px-3 py-1.5 text-sm font-medium text-white ${photosComplete ? 'bg-brand-700 hover:bg-brand-800' : 'bg-slate-400 cursor-not-allowed'}`}
                          title={photosComplete
                            ? 'Request the Work Completion Certificate after all works are finished'
                            : `Upload restoration photos for: ${missingStages.join(', ')}`}
                        >
                          Request Completion Certificate
                        </button>
                        {!photosComplete && (
                          <span className="text-xs text-amber-700">
                            Upload {missingStages.join(', ')} photos below to enable this.
                          </span>
                        )}
                      </div>
                    )}
                    {isApproved && completionIssued && (
                      <button
                        onClick={() => window.open(`${API_BASE_URL}/applications/${applicationId}/completion-certificate`, '_blank')}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700"
                      >
                        View Completion Certificate
                      </button>
                    )}
                    {isApproved && !completionIssued && completionRequested && isL2Officer && (
                      <button
                        onClick={handleGenerateCompletion}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700"
                        title="Approve the applicant's request and issue the Work Completion Certificate"
                      >
                        Approve &amp; Generate Completion Certificate
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Restoration Progress — visible once the NOC is approved. NGO
                uploads Before/During/After photos (max 5 per stage); L1, L2,
                Dept Admins view only. */}
            {(appStatusUpper === 'APPROVED' || appStatusUpper === 'DEEMED_APPROVED') && (() => {
              const isApplicant = Number(application?.applied_by) === Number(userId);
              const STAGES = [
                { key: 'BEFORE', label: 'Before Work', tone: 'bg-slate-50 border-slate-200' },
                { key: 'DURING', label: 'During Work', tone: 'bg-amber-50 border-amber-200' },
                { key: 'AFTER',  label: 'After Work',  tone: 'bg-emerald-50 border-emerald-200' },
              ];
              const MAX = 5;
              const photosByStage = (stage) => restorationPhotos.filter((p) => p.stage === stage);
              const refetch = async () => {
                try {
                  const r = await API.get(`/restoration/${applicationId}`);
                  const d = r?.data?.data;
                  if (d && Array.isArray(d.photos)) setRestorationPhotos(d.photos);
                } catch (_) {}
              };
              const handleUpload = async (stage, files) => {
                if (!files || files.length === 0) return;
                const existing = photosByStage(stage).length;
                if (existing + files.length > MAX) {
                  showError(`This stage allows only ${MAX} photos in total. Currently ${existing} uploaded.`);
                  return;
                }
                const fd = new FormData();
                fd.append('stage', stage);
                Array.from(files).forEach((f) => fd.append('photos', f));
                setRestorationBusy(stage);
                try {
                  await API.post(`/restoration/${applicationId}`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                  });
                  showSuccess('Photos uploaded');
                  await refetch();
                } catch (err) {
                  showError(err?.response?.data?.message || 'Upload failed');
                } finally {
                  setRestorationBusy('');
                }
              };
              const handleDelete = async (photoId) => {
                if (!window.confirm('Remove this photo?')) return;
                try {
                  await API.delete(`/restoration/photo/${photoId}`);
                  await refetch();
                } catch (err) {
                  showError(err?.response?.data?.message || 'Delete failed');
                }
              };

              return (
                <div className="bg-white border border-slate-200 border-t-4 border-t-emerald-600 p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <Camera className="w-5 h-5 text-brand-700" />
                    <h3 className="text-base font-semibold text-gray-900">Restoration Progress Photos</h3>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">
                    {isApplicant
                      ? `Upload up to ${MAX} geo-tagged photos per stage documenting the restoration work. Officers and the Department Admin can view these.`
                      : 'Photos uploaded by the applicant organization documenting the restoration work.'}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {STAGES.map((s) => {
                      const items = photosByStage(s.key);
                      const canAddMore = isApplicant && items.length < MAX;
                      return (
                        <div key={s.key} className={`border p-3 ${s.tone}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-gray-800">{s.label}</span>
                            <span className="text-xs text-gray-500">{items.length}/{MAX}</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 min-h-[80px]">
                            {items.map((p) => (
                              <div key={p.photo_id} className="relative group">
                                <a href={`${FILE_BASE_URL}${p.stored_path.replace('/uploads', '')}`} target="_blank" rel="noreferrer">
                                  <img
                                    src={`${FILE_BASE_URL}${p.stored_path.replace('/uploads', '')}`}
                                    alt={p.original_name}
                                    className="w-full h-16 object-cover border border-white shadow-sm"
                                  />
                                </a>
                                {isApplicant && (
                                  <button
                                    type="button"
                                    onClick={() => handleDelete(p.photo_id)}
                                    className="absolute top-0 right-0 bg-red-600 text-white px-1 py-0.5 opacity-0 group-hover:opacity-100 transition"
                                    title="Remove photo"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            ))}
                            {items.length === 0 && !isApplicant && (
                              <div className="col-span-3 text-xs text-gray-400 italic flex items-center">Not uploaded yet</div>
                            )}
                          </div>
                          {canAddMore && (
                            <label className="mt-3 flex items-center justify-center gap-2 text-xs px-2 py-1.5 bg-white border border-dashed border-gray-400 cursor-pointer hover:border-brand-600 hover:text-brand-700 text-gray-600">
                              <Upload className="w-3.5 h-3.5" />
                              {restorationBusy === s.key ? 'Uploading…' : `Add photo${items.length + 1 === MAX ? '' : 's'}`}
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg"
                                multiple
                                className="hidden"
                                disabled={!!restorationBusy}
                                onChange={(e) => { handleUpload(s.key, e.target.files); e.target.value = ''; }}
                              />
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </>
        )}

        {/* NGO Profile Modal */}
        {showNGOProfile && ngo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="px-6 py-4 bg-brand-800 border-b-4 border-brand-accent">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-6 h-6 text-white" />
                    <h3 className="text-lg font-semibold text-white">Applicant Profile</h3>
                  </div>
                  <button
                    onClick={() => setShowNGOProfile(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-160px)] space-y-5">
                <div className="bg-brand-50 border border-brand-100 p-5">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 bg-brand-700 text-white flex items-center justify-center text-xl font-semibold">
                      {(ngo.full_name || 'A').trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wider text-brand-700 font-semibold">Organization / Applicant</p>
                      <p className="text-lg font-semibold text-gray-900 truncate">{ngo.full_name || '-'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Email</p>
                      <p className="text-sm text-brand-700 mt-0.5 break-all">{ngo.email_id || '-'}</p>
                    </div>
                    {ngo.mobile_number && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Mobile</p>
                        <p className="text-sm text-gray-900 mt-0.5">{ngo.mobile_number}</p>
                      </div>
                    )}
                    {ngo.pan_number && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">PAN</p>
                        <p className="text-sm font-mono text-gray-900 mt-0.5">{ngo.pan_number}</p>
                      </div>
                    )}
                    {ngo.gst_number && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">GST</p>
                        <p className="text-sm font-mono text-gray-900 mt-0.5">{ngo.gst_number}</p>
                      </div>
                    )}
                  </div>
                </div>

                {(() => {
                  const docs = [
                    ['Registration Certificate', ngo.registration_cretificate_path],
                    ['PAN Card', ngo.pan_card_path],
                    ['Organization Profile', ngo.organization_profile_path],
                    ['Similar Project Details', ngo.similar_project_details_path],
                    // Individuals now upload their Aadhaar Card Copy in this slot.
                    [String(ngo.user_type).toUpperCase() === 'OTHERS' ? 'Aadhaar Card Copy' : 'Resolution Copy', ngo.resolution_copy_path],
                  ].filter(([, p]) => typeof p === 'string' && p.trim().length > 0);

                  if (docs.length === 0) return null;

                  const isDataUri = (raw) => /^data:/i.test(raw);
                  // Show a clean filename only (strip the upload directory + the
                  // multer hash-and-timestamp suffix we add at upload time).
                  const prettyName = (raw) => {
                    if (isDataUri(raw)) return 'Inline image (legacy upload)';
                    const base = raw.split('/').pop() || raw;
                    return base.replace(/_\d{10,}-\d+(?=\.[^.]+$)/, '');
                  };
                  const fileUrl = (raw) =>
                    isDataUri(raw) ? raw : `${FILE_BASE_URL}/${raw.replace(/^uploads\//, '')}`;

                  const isImagePath = (raw) =>
                    isDataUri(raw) || /\.(png|jpe?g|gif|webp)$/i.test(raw || '');
                  return (
                    <div>
                      <div className="flex items-center gap-2.5 mb-3">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-brand-100">
                          <FileText className="w-4 h-4 text-brand-700" />
                        </span>
                        <h4 className="text-base font-semibold text-gray-900 tracking-tight">Registration Documents</h4>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {docs.map(([label, raw]) => {
                          const ext = (prettyName(raw) || '').split('.').pop().toUpperCase().slice(0, 4) || 'FILE';
                          const isImg = isImagePath(raw);
                          const isPdf = /\.pdf$/i.test(raw || '');
                          const badge = isImg
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : isPdf
                            ? 'bg-red-100 text-red-700 border-red-200'
                            : 'bg-slate-100 text-slate-700 border-slate-200';
                          return (
                            <div
                              key={label}
                              className="bg-white border border-gray-200 overflow-hidden hover:border-brand-100 hover:shadow-md transition"
                            >
                              {isImg ? (
                                <a href={fileUrl(raw)} target="_blank" rel="noreferrer" className="block bg-gray-50 border-b border-gray-100">
                                  <img
                                    src={fileUrl(raw)}
                                    alt={label}
                                    className="w-full h-32 object-cover"
                                    loading="lazy"
                                  />
                                </a>
                              ) : (
                                <div className="w-full h-32 bg-slate-50 flex items-center justify-center border-b border-gray-100">
                                  <FileText className="w-10 h-10 text-slate-400" />
                                </div>
                              )}
                              <div className="p-2.5">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 border ${badge}`}>{ext}</span>
                                  <p className="text-xs font-semibold text-gray-900 truncate">{label}</p>
                                </div>
                                <p className="text-[11px] text-gray-500 truncate mb-2" title={prettyName(raw)}>
                                  {prettyName(raw)}
                                </p>
                                <button
                                  onClick={() => window.open(fileUrl(raw), '_blank')}
                                  className="w-full inline-flex items-center justify-center gap-1 px-2 py-1 text-xs font-medium text-brand-800 bg-brand-50 border border-brand-100 hover:bg-brand-100"
                                >
                                  <Eye className="w-3 h-3" /> Open
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                <button
                  onClick={() => setShowNGOProfile(false)}
                  className="px-6 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
